import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import {
  Company,
  CompanyRelationshipType,
  CompanySize,
  CompanySector,
  CompanyTaxType,
  CompanyStateLeadResponse,
  CompanyCycleResponse,
  ContactCompanyResponse,
  ContactCompanyWithDetails
} from '../models/empresa.model';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DirectoryUser } from '../../../shared/services/user-directory-api.service';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import { ContactCompanyApiService } from '../services/contact-company-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { WorkflowRecordHistoryComponent } from '../../automation/workflows/workflow-record-history/workflow-record-history.component';

@Component({
  selector: 'app-empresa-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    QuickActionsMenuComponent,
    AuditTimelineComponent,
    WorkflowRecordHistoryComponent
  ],
  templateUrl: './empresa-detail-modal.component.html'
})
export class EmpresaDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() company: Company | null = null;
  @Input() relationshipTypes: CompanyRelationshipType[] = [];
  @Input() sizes: CompanySize[] = [];
  @Input() sectors: CompanySector[] = [];
  @Input() taxTypes: CompanyTaxType[] = [];
  @Input() stateLeads: CompanyStateLeadResponse[] = [];
  @Input() cycles: CompanyCycleResponse[] = [];
  @Output() close = new EventEmitter<void>();

  // Contacts section
  contacts: ContactCompanyWithDetails[] = [];
  loadingContacts = false;
  contactsError = '';

  registeredByUser: any = null;
  ownerUser: any = null;
  loadingUsers = false;

  // Audit timeline
  activeTab: 'info' | 'timeline' | 'automatizaciones' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  constructor(
    private contactCompanyApi: ContactCompanyApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Load contacts whenever the modal opens with a company
    if (changes['isOpen'] || changes['company']) {
      if (this.isOpen && this.company?.id) {
        this.cargarContactos(this.company.id);
        this.cargarUsuarios();
      } else if (!this.isOpen) {
        // Reset when closing
        this.contacts = [];
        this.contactsError = '';
        this.registeredByUser = null;
        this.ownerUser = null;
        this.activeTab = 'info';
        this.timelineLogs = [];
        this.timelineError = '';
        this.timelinePage = 0;
      }
    }
  }

  private cargarUsuarios(): void {
    const registerId = this.company?.userRegisterId;
    const ownerId = this.company?.userPropietyId;

    if (!registerId && !ownerId) return;

    this.loadingUsers = true;
    this.registeredByUser = null;
    this.ownerUser = null;

    const currentUser = this.authService.getCurrentUser();
    // El usuario en sesión ya está en memoria: se evita la llamada. Se normaliza al shape del
    // directorio (`mail`, no `email`) para que ambas ramas devuelvan exactamente lo mismo.
    const sessionUser: DirectoryUser | null = currentUser
      ? {
          id: currentUser.id,
          fullname: currentUser.fullname,
          lastname: currentUser.lastname,
          mail: currentUser.email,
        }
      : null;
    const resolve = (id: number | undefined): Observable<DirectoryUser | null> =>
      id && id === currentUser?.id
        ? of(sessionUser)
        : id
          ? this.authApiService.getUserById(id)
          : of(null);

    if (registerId === ownerId) {
      resolve(registerId).subscribe({
        next: (user) => {
          this.registeredByUser = user;
          this.ownerUser = user;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    } else {
      forkJoin([resolve(registerId), resolve(ownerId)]).subscribe({
        next: ([registerUser, ownerUser]) => {
          this.registeredByUser = registerUser;
          this.ownerUser = ownerUser;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    }
  }

  /**
   * "Creado por" de una empresa que nadie creó a mano. Mismo criterio que Contactos:
   * una empresa nacida de un formulario público no tiene usuario detrás (el backend
   * deja `userRegisterId` nulo a propósito), y eso es un autor conocido —el sistema—,
   * no un dato perdido. "Sin información" se reserva para cuando SÍ hay id pero el
   * usuario no se pudo resolver (borrado, otro negocio).
   */
  get createdByLabel(): string {
    if (this.registeredByUser) {
      return `${this.registeredByUser.fullname} ${this.registeredByUser.lastname || ''}`.trim();
    }
    return this.company?.userRegisterId ? 'Sin información' : 'Sistema';
  }

  /** Mismo criterio para el responsable: sin dueño asignado ≠ dato perdido. */
  get ownerLabel(): string {
    if (this.ownerUser) {
      return `${this.ownerUser.fullname} ${this.ownerUser.lastname || ''}`.trim();
    }
    return this.company?.userPropietyId ? 'Sin información' : 'Sin asignar';
  }

  private cargarContactos(idCompany: number): void {
    this.loadingContacts = true;
    this.contactsError = '';
    this.contacts = [];

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactCompanyApi.getByCompany(idCompany, businessId).subscribe({
      next: (associations: ContactCompanyResponse[]) => {
        this.contacts = associations.map(assoc => ({
          ...assoc,
          contactFullname: assoc.contact?.fullname ?? `Contacto #${assoc.idContact}`,
          contactLastname: assoc.contact?.lastname,
          contactMail: assoc.contact?.mail,
          contactPhone: assoc.contact?.phone
        }));
        this.loadingContacts = false;
      },
      error: () => {
        this.loadingContacts = false;
        this.contactsError = 'Error al cargar los contactos asociados';
      }
    });
  }

  getContactInitials(entry: ContactCompanyWithDetails): string {
    const first = entry.contactFullname ? entry.contactFullname.charAt(0).toUpperCase() : '';
    const last = entry.contactLastname
      ? entry.contactLastname.charAt(0).toUpperCase()
      : (entry.contactFullname && entry.contactFullname.length > 1
          ? entry.contactFullname.charAt(1).toUpperCase()
          : '');
    return first + last;
  }

  getContactAvatarColor(entry: ContactCompanyWithDetails): string {
    const colors = [
      'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    ];
    const name = entry.contactFullname || '';
    const charSum = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  }

  get initials(): string {
    if (!this.company) return '';
    const commercialName = this.company.commercialName || '';
    return commercialName.substring(0, 2).toUpperCase();
  }

  get avatarColorClass(): string {
    if (!this.company?.commercialName) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];

    const index = this.company.commercialName
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return colors[index % colors.length];
  }

  getRelationshipTypeName(): string {
    if (!this.company?.idCompanyRelationshipType) return '—';
    const type = this.relationshipTypes.find(t => t.id === this.company?.idCompanyRelationshipType);
    return type?.name || '—';
  }

  getSizeName(): string {
    if (!this.company?.idCompanySize) return '—';
    const size = this.sizes.find(s => s.id === this.company?.idCompanySize);
    return size?.name || '—';
  }

  getSectorName(): string {
    if (!this.company?.idCompanySector) return '—';
    const sector = this.sectors.find(s => s.id === this.company?.idCompanySector);
    return sector?.name || '—';
  }

  getTaxTypeName(): string {
    if (!this.company?.idCompanyTaxType) return '—';
    const taxType = this.taxTypes.find(t => t.id === this.company?.idCompanyTaxType);
    return taxType?.name || '—';
  }

  getStateLeadName(): string {
    if (!this.company?.idCompanyStateLead) return '—';
    const stateLead = this.stateLeads.find(s => s.id === this.company?.idCompanyStateLead);
    return stateLead?.name || '—';
  }

  getCycleName(): string {
    if (!this.company?.idCompanyCycle) return '—';
    const cycle = this.cycles.find(c => c.id === this.company?.idCompanyCycle);
    return cycle?.name || '—';
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  // ── Timeline ──

  onTabChange(tab: 'info' | 'timeline' | 'automatizaciones'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && this.timelineLogs.length === 0 && !this.loadingTimeline) {
      this.loadTimeline(0);
    }
  }

  loadTimeline(page = 0): void {
    if (!this.company?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi.getLogs({ entityName: 'company', idEntity: String(this.company.id), page, size: 15 }).subscribe({
      next: data => {
        this.timelineLogs = data.content ?? [];
        this.timelinePage = data.page ?? page;
        this.timelineTotalPages = data.totalPages ?? 1;
        this.timelineTotalElements = data.totalElements ?? 0;
        this.loadingTimeline = false;
      },
      error: () => {
        this.timelineError = 'Error al cargar la línea de tiempo.';
        this.loadingTimeline = false;
      }
    });
  }

  // La presentación de los eventos del timeline (traducciones, diffs, títulos)
  // y el expand/colapso por log viven en AuditTimelineComponent / AuditDisplayService.

  onClose(): void {
    this.close.emit();
  }
}
